import tenantConstants from '@constants';
import tenantUtils from '@utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Styled from 'styled-components';
import { Icon } from '../../../../../components/common';
import { formatNumberString } from '../../../../../utility/utility';
import MediaGalleryModal from '../../../../../components/post-listing/media-gallery/media-gallery-modal';

/** Room sub-type id (listing_type.id) — beds/Studio isn't applicable for Room listings. */
const ROOM_PROPERTY_TYPE_ID = 27;

/* Realistic iPhone frame overlay (top of the phone, with dynamic island baked in). */
const PHONE_FRAME = `${process.env.PUBLIC_URL || ''}/profolio-assets/images/post-listing/iphone-mockup-2.png`;

const PreviewWrapper = Styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px 12px 0;
  width: 100%;
  height: 430px;
  overflow: hidden;
`;

const PreviewTitle = Styled.p`
  color: #222;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  margin-bottom: 0px;
`;

/* Realistic iPhone frame: a PNG overlay (rounded top, dynamic island, side rails) sits on top and
   the preview content shows through its transparent screen cutout. Only the top of the phone is
   framed — the bottom is open, so the listing content continues below (clipped by the card). */
const Phone = Styled.div`
  position: relative;
  margin: 0 auto;
  /* Screen cutout inset: ~8.5% each side (≈12px in from the visible frame edge) so content doesn't
     tuck under the bezel; the top padding clears the dynamic island so content starts on the glass. */
  padding: 62px 8.5% 0;
`;

const FrameOverlay = Styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  z-index: 3;
  pointer-events: none;
  user-select: none;
`;

const Screen = Styled.div`
  position: relative;
  z-index: 1;
  background: #fff;
  overflow: hidden;
  min-height: 360px;
`;

/* Faux iOS status bar — sits at the dynamic-island level, flanking the (baked-in) island.
   Absolutely positioned over the glass so time/battery read on the white screen. */
const StatusBar = Styled.div`
  position: absolute;
  top: 45px;
  inset-inline: 10%;
  padding: 0px 7px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 700;
  color: #1d1d1f;
  pointer-events: none;

  .left { display: flex; align-items: center; gap: 6px; }
  .right { display: flex; align-items: center; gap: 6px; }
`;

/* Image has top + side margins and a subtle border */
const Cover = Styled.div`
  position: relative;
  margin: 12px 8px 0;
  width: calc(100% - 16px);
  height: 168px;
  background-color: #E0EEF2;
  background-image: ${({ $src }) => ($src ? `url(${$src})` : 'none')};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  border: 1px solid #E0E0E0;
  overflow: hidden;
`;

const CoverPlaceholder = Styled.div`
  position: absolute;
  inset: 0;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;

  svg { width: 100%; height: 100%; }
`;

/* Empty-cover placeholder (Figma) — light-gray card with a gray image glyph. */
const COVER_PLACEHOLDER = (
  <svg viewBox="0 0 254 147" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <rect width="254" height="147" rx="5" fill="#F5F5F5" />
    <g opacity="0.36">
      <path d="M140.579 89.0171H146.144V94.5944H140.579V89.0171Z" fill="#9D9D9D" />
      <path d="M130.441 58.6658C131.521 57.7056 132.911 57.1769 134.355 57.1769C135.799 57.1769 137.189 57.7057 138.269 58.6658L139.821 60.0455V55.116C139.821 47.0015 133.233 40.3999 125.136 40.3999C118.043 40.3999 112.108 45.4654 110.745 52.176C112.975 55.5697 116.882 62.2035 118.554 69.2333L130.441 58.6658Z" fill="#9D9D9D" />
      <path d="M158.652 103.652H155.937V84.9058L157.217 86.0433C157.591 86.3761 158.057 86.5397 158.521 86.5396C159.063 86.5396 159.604 86.3157 159.992 85.8768C160.713 85.0628 160.638 83.8176 159.826 83.0956L154.063 77.9724V67.762C154.063 66.674 153.182 65.7919 152.097 65.7919C151.011 65.7919 150.13 66.674 150.13 67.762V74.4769L135.66 61.6131C134.915 60.9514 133.795 60.9514 133.051 61.6131L108.885 83.0955C108.072 83.8175 107.998 85.0627 108.718 85.8767C109.439 86.6907 110.682 86.765 111.494 86.043L112.773 84.9055V103.651H106.428V95.0562C107.268 94.799 108.076 94.3766 108.842 93.7976V90.3231C107.666 90.0464 106.592 89.412 105.777 88.4917C103.619 86.0538 103.843 82.3109 106.275 80.1481L115.149 72.2598C113.655 62.7109 106.387 52.7548 106.044 52.2891C105.673 51.7864 105.086 51.4897 104.463 51.4897C103.839 51.4897 103.252 51.7864 102.881 52.2891C102.499 52.8082 93.5078 65.1223 93.5078 75.5179C93.5078 80.5918 94.5175 85.393 96.3511 89.0369C97.9684 92.2513 100.104 94.3248 102.497 95.0564V103.652H95.3474C94.2616 103.652 93.3813 104.534 93.3813 105.622C93.3813 106.71 94.2616 107.592 95.3474 107.617H155.937H158.653C159.738 107.592 160.619 106.71 160.619 105.622C160.619 104.534 159.738 103.652 158.652 103.652ZM136.646 87.0466C136.646 85.9586 137.526 85.0765 138.612 85.0765H148.113C149.199 85.0765 150.079 85.9586 150.079 87.0466V96.5674C150.079 97.6554 149.199 98.5374 148.113 98.5374H138.612C137.526 98.5374 136.646 97.6554 136.646 96.5674V87.0466ZM125.88 103.652V89.0167H121.948V103.652H118.016V87.0466C118.016 85.9585 118.897 85.0765 119.982 85.0765H127.846C128.932 85.0765 129.812 85.9586 129.812 87.0466V103.652H125.88Z" fill="#9D9D9D" />
    </g>
  </svg>
);

const NavArrow = Styled.span`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 8px;' : 'right: 8px;')}
  transform: translateY(-50%);
  width: 22px; height: 22px;
  border-radius: 50%;
  background: rgba(255,255,255,0.92);
  display: flex; align-items: center; justify-content: center;
  color: #444; font-size: 14px; font-weight: 400;
  box-shadow: 0 1px 4px rgba(0,0,0,0.18);
  /* Keep the chevron glyphs (‹ ›) from bidi-mirroring in the Arabic (RTL) UI. */
  direction: ltr;
`;

const Dots = Styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 5px;
  span { width: 4px; height: 4px; border-radius: 50%; background: rgba(255,255,255,0.6); }
  span.active { background: #fff; }
`;

const TruBroker = Styled.div`
  position: absolute;
  bottom: 8px;
  /* Leading corner: bottom-left in en (LTR), bottom-right in ar (RTL) — opposite the heart. */
  inset-inline-start: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  /* Logical padding: tight on the avatar (leading) side, roomy on the text side — flips in RTL.
     Bar height is fixed so the avatar can grow past it (see .av). */
  padding-block: 0;
  /* No leading padding: the avatar sits flush at the extreme leading end (see .av margin). */
  padding-inline-start: 0;
  padding-inline-end: 10px;
  min-height: 20px;
  border-radius: 999px;
  background: linear-gradient(90deg, #0A5158 0%, #16847D 55%, #0C5A61 100%);
  color: #fff;
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);

  /* Circular broker avatar on the leading edge. Bigger than the 20px bar (26px, with negative
     block margin so it overflows equally top/bottom without stretching the pill). The white→teal
     ring is a gradient border via the border-box technique; the photo (--tb-av) sits in padding-box. */
  .av {
    box-sizing: border-box;
    width: 26px;
    height: 26px;
    /* Overflow the bar top/bottom, and sit flush at the extreme leading end (covers the pill's
       rounded cap so no bar shows before the avatar). Logical inline-start flips for en/ar. */
    margin-block: -3px;
    margin-inline-start: -3px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1.5px solid transparent;
    background:
      var(--tb-av, none) center / cover no-repeat padding-box,
      linear-gradient(#cdd6da, #cdd6da) padding-box,
      linear-gradient(-90deg, #fff 0%, #098f79 100%) border-box;
  }
  /* Brand always reads left-to-right even in Arabic; "Broker" bold, ™ superscript. */
  .tb-txt { direction: ltr; }
  .tb-b { font-weight: 800; }
  .tb-tm { font-size: 0.6em; vertical-align: super; font-weight: 600; margin-inline-start: 1px; }
`;

/* Solid gray heart with white border — no circle background */
const Heart = Styled.span`
  position: absolute;
  bottom: 8px;
  inset-inline-end: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageCounter = Styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 7px;
  line-height: 1.5;
  z-index: 2;
  letter-spacing: 0.3px;
`;

const CardContent = Styled.div`
  padding: 12px;
`;

const PriceText = Styled.p`
  color: #444444;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* MetaRow, TitleText, LocationRow — same font-size and project gray */
const MetaRow = Styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #707070;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 5px;

  span { display: inline-flex; align-items: center; gap: 4px; }
`;

const TitleText = Styled.p`
  color: #707070;
  font-size: 12px;
  font-weight: 500;
  margin: 0 0 4px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LocationRow = Styled.p`
  color: #707070;
  font-size: 12px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  svg { flex-shrink: 0; }
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

/* Location pin glyph shown before the listing location (Figma). */
const LOCATION_PIN = (
  <svg width="8" height="10" viewBox="0 0 8 10" fill="none" aria-hidden="true">
    <path
      d="M4.0002 9.56681L3.71858 9.24714C3.58996 9.10253 0.575195 5.65013 0.575195 3.18717C0.575195 2.25481 0.935962 1.36051 1.57834 0.702147C1.89271 0.377346 2.269 0.118869 2.68498 -0.058007C3.10096 -0.234883 3.54818 -0.326566 4.0002 -0.327637C4.9082 -0.327637 5.77967 0.0430245 6.42205 0.702147C7.06716 1.36833 7.42704 2.25983 7.4252 3.18717C7.4252 5.65013 4.41043 9.10177 4.28181 9.24714L4.0002 9.56681ZM4.0002 0.460113C3.29465 0.460113 2.61802 0.747052 2.11873 1.25852C1.61717 1.77528 1.33654 2.46703 1.33631 3.18717C1.33631 4.94534 3.2642 7.47756 4.0002 8.38176C4.73619 7.47756 6.66408 4.9423 6.66408 3.18717C6.66385 2.46703 6.38322 1.77528 5.88166 1.25852C5.63722 1.0066 5.34481 0.806167 5.02167 0.669044C4.69854 0.531921 4.35122 0.460882 4.0002 0.460113ZM4.0002 2.165C3.79707 2.16359 3.59829 2.22384 3.43012 2.33777C3.25981 2.4531 3.1274 2.61622 3.04957 2.80662C2.97153 2.99828 2.95048 3.2084 2.98896 3.41173C3.02744 3.61506 3.12381 3.80296 3.26648 3.95285C3.40957 4.10051 3.59148 4.20097 3.79013 4.24207C3.98802 4.28317 4.19428 4.26339 4.38151 4.18423C4.56875 4.10507 4.72934 3.97112 4.84198 3.79759C4.93618 3.65347 4.99417 3.48873 5.011 3.31738C5.02783 3.14603 5.00301 2.97316 4.93865 2.81347C4.88831 2.68716 4.81386 2.57186 4.71945 2.47401C4.62589 2.37757 4.51417 2.30062 4.39072 2.24758C4.26728 2.19454 4.13455 2.16648 4.0002 2.165Z"
      fill="#707070"
    />
  </svg>
);

/* All three buttons: equal width, reduced height, smaller radius, smaller font/icons */
const ContactRow = Styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  pointer-events: none;
`;

const PillBtn = Styled.div`
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  flex: 1;
  background: ${({ $variant }) => ($variant === 'whatsapp' ? '#E7F6EC' : '#E9F1F1')};
  color: ${({ $variant }) => ($variant === 'whatsapp' ? '#1DA851' : '#006169')};

  svg { flex-shrink: 0; }
`;

/* Contact pill icons (Figma). */
const PHONE_ICON = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M16.125 13.375C15.1531 13.3978 14.1835 13.2713 13.25 13C13.1062 12.9413 12.9493 12.9219 12.7955 12.9439C12.6417 12.9658 12.4966 13.0283 12.375 13.125L11.125 14.875C8.62662 13.6449 6.60505 11.6233 5.37499 9.12498L6.99998 7.62499C7.07678 7.49197 7.11721 7.34108 7.11721 7.18749C7.11721 7.03389 7.07678 6.883 6.99998 6.74999C6.72464 5.81733 6.59808 4.84708 6.62499 3.875C6.62499 3.76009 6.60235 3.64631 6.55838 3.54015C6.51441 3.43399 6.44996 3.33753 6.3687 3.25628C6.28745 3.17503 6.191 3.11058 6.08484 3.06661C5.97868 3.02263 5.86489 3 5.74999 3H3C2.5 3 2 3.25 2 3.875C2.03263 7.61107 3.53127 11.1849 6.17317 13.8268C8.81507 16.4687 12.3889 17.9673 16.125 18C16.2512 18.0013 16.3762 17.9752 16.4914 17.9236C16.6066 17.8721 16.7093 17.7962 16.7924 17.7012C16.8756 17.6062 16.9371 17.4943 16.973 17.3733C17.0088 17.2523 17.018 17.1249 17 17V14.25C16.9697 14.0402 16.8643 13.8485 16.7034 13.7106C16.5424 13.5726 16.3369 13.4978 16.125 13.5V13.375Z" fill="#006169" />
  </svg>
);

const WHATSAPP_ICON = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <g clipPath="url(#clip0_51305_11097)">
      <path fillRule="evenodd" clipRule="evenodd" d="M16.3038 3.61584C14.6199 1.92988 12.3803 1.00099 9.9944 1C5.07801 1 1.07679 5.00111 1.07482 9.91881C1.07416 11.4908 1.48483 13.0254 2.26541 14.3781L1 19L5.72841 17.7596C7.03127 18.4703 8.49806 18.8449 9.99077 18.8453H9.99451C14.9103 18.8453 18.912 14.8439 18.9139 9.92595C18.9149 7.54258 17.9879 5.3017 16.3038 3.61584ZM9.9944 17.339H9.99132C8.6611 17.3384 7.35647 16.9809 6.21807 16.3056L5.94747 16.1449L3.14156 16.881L3.8905 14.1453L3.71417 13.8648C2.97205 12.6844 2.58016 11.3201 2.58082 9.91936C2.58236 5.8319 5.90814 2.50645 9.99736 2.50645C11.9775 2.5071 13.839 3.27922 15.2386 4.68053C16.6383 6.08184 17.4086 7.94446 17.408 9.9254C17.4062 14.0132 14.0807 17.339 9.9944 17.339ZM14.0609 11.7866C13.8381 11.675 12.7423 11.136 12.538 11.0615C12.3338 10.9871 12.1851 10.9501 12.0366 11.1731C11.888 11.3961 11.461 11.8982 11.3309 12.0469C11.2008 12.1956 11.0709 12.2143 10.848 12.1027C10.6251 11.9912 9.90705 11.7557 9.05572 10.9965C8.39325 10.4055 7.946 9.67568 7.81592 9.45266C7.68606 9.22942 7.81482 9.12043 7.9137 8.99783C8.15496 8.69823 8.39654 8.38413 8.47081 8.23549C8.54519 8.08673 8.50795 7.95654 8.45214 7.84503C8.39654 7.73352 7.95083 6.63654 7.76516 6.19016C7.58411 5.75576 7.40052 5.81443 7.26363 5.80762C7.13378 5.80114 6.98513 5.79982 6.83649 5.79982C6.68795 5.79982 6.44647 5.85552 6.24213 6.07876C6.03789 6.30189 5.46221 6.84099 5.46221 7.93798C5.46221 9.03496 6.2608 10.0947 6.3722 10.2435C6.48361 10.3922 7.9438 12.6433 10.1794 13.6086C10.7111 13.8384 11.1262 13.9754 11.45 14.0781C11.9839 14.2478 12.4696 14.2238 12.8537 14.1665C13.2819 14.1024 14.1722 13.6272 14.3581 13.1067C14.5437 12.5861 14.5437 12.1399 14.4879 12.0469C14.4323 11.9539 14.2837 11.8982 14.0609 11.7866Z" fill="#28B16D" />
    </g>
    <defs>
      <clipPath id="clip0_51305_11097">
        <rect width="18" height="18" fill="white" transform="translate(1 1)" />
      </clipPath>
    </defs>
  </svg>
);

const BED_ICON = (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <g clipPath="url(#clip0_51305_10999)">
      <path d="M4.04508 10.9115V4.53125H2.22217V15.4688H4.04508V13.6458H15.9548V15.4688H17.7777V13.6458V10.9115H4.04508Z" fill="#767676" />
      <path d="M6.32373 7.26562C5.56865 7.26562 4.95654 7.87773 4.95654 8.63281V10H7.69092V8.63281C7.69092 7.87773 7.07881 7.26562 6.32373 7.26562Z" fill="#767676" />
      <path d="M16.4102 7.26562H9.96924C9.21416 7.26562 8.60205 7.87773 8.60205 8.63281V10H17.7774V8.63281C17.7774 7.87773 17.1653 7.26562 16.4102 7.26562Z" fill="#767676" />
    </g>
    <defs>
      <clipPath id="clip0_51305_10999">
        <rect width="15.5556" height="15.5556" fill="white" transform="translate(2.22217 2.22217)" />
      </clipPath>
    </defs>
  </svg>
);

const BATH_ICON = (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M2.5 11.5C2.5 11.125 2.875 10.75 3.25 10.75H16.75C17.125 10.75 17.5 11.125 17.5 11.5C17.5 11.875 17.125 12.25 16.75 12.25H3.25C3.05109 12.25 2.86032 12.171 2.71967 12.0303C2.57902 11.8897 2.5 11.6989 2.5 11.5Z" fill="#767676" />
    <path d="M3.25 10.7502H16.75V14.0502C16.75 14.3684 16.6236 14.6737 16.3985 14.8987C16.1735 15.1237 15.8683 15.2502 15.55 15.2502H4.525C4.36122 15.2604 4.19708 15.237 4.04272 15.1813C3.88836 15.1256 3.74706 15.0388 3.62756 14.9264C3.50806 14.8139 3.4129 14.6781 3.34796 14.5274C3.28302 14.3767 3.24968 14.2143 3.25 14.0502V10.7502ZM5.5 15.2502H7V16.3002C7.0015 16.4076 6.96452 16.512 6.89575 16.5945C6.82697 16.6771 6.73093 16.7323 6.625 16.7502H5.875C5.76907 16.7323 5.67304 16.6771 5.60426 16.5945C5.53548 16.512 5.4985 16.4076 5.5 16.3002V15.2502ZM13 15.2502H14.5V16.3002C14.5015 16.4076 14.4645 16.512 14.3957 16.5945C14.327 16.6771 14.2309 16.7323 14.125 16.7502H13.375C13.2691 16.7323 13.173 16.6771 13.1043 16.5945C13.0355 16.512 12.9985 16.4076 13 16.3002V15.2502ZM5.35 4.52518L7.3 3.40018C7.39185 3.36417 7.49299 3.35935 7.58785 3.38645C7.68271 3.41356 7.76604 3.47108 7.825 3.55018L8.35 4.52518L5.8 6.02518L5.2 5.05018C5.164 4.95833 5.15918 4.85719 5.18628 4.76233C5.21338 4.66747 5.27091 4.58414 5.35 4.52518Z" fill="#767676" />
    <path d="M2.87548 11.1249V4.59992C2.84431 4.34765 2.86476 4.09168 2.93558 3.84756C3.0064 3.60344 3.12612 3.37627 3.28747 3.17985C3.44881 2.98344 3.6484 2.82188 3.87412 2.70499C4.09984 2.58811 4.34696 2.51833 4.60048 2.49992C4.70033 2.49221 4.80063 2.49221 4.90048 2.49992C7.07548 2.49992 7.15048 3.69992 7.15048 3.77492C7.01385 4.0327 6.86364 4.28305 6.70048 4.52492C6.70048 4.52492 6.02548 4.89992 5.95048 4.52492C5.87548 4.14992 5.50048 3.99992 5.05048 3.99992C4.60048 3.99992 4.37548 4.29992 4.37548 4.89992V11.1249H2.87548Z" fill="#767676" />
  </svg>
);

const AREA_ICON = (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M16.75 2.5H3.25C3.05109 2.5 2.86032 2.57902 2.71967 2.71967C2.57902 2.86032 2.5 3.05109 2.5 3.25V16.75C2.5 16.9489 2.57902 17.1397 2.71967 17.2803C2.86032 17.421 3.05109 17.5 3.25 17.5H16.75C16.9489 17.5 17.1397 17.421 17.2803 17.2803C17.421 17.1397 17.5 16.9489 17.5 16.75V3.25C17.5 3.05109 17.421 2.86032 17.2803 2.71967C17.1397 2.57902 16.9489 2.5 16.75 2.5ZM16 9.25H10.75V4H16V9.25ZM9.25 4V9.25H4V4H9.25ZM4 10.75H9.25V16H4V10.75ZM10.75 16V10.75H16V16H10.75Z" fill="#767676" />
  </svg>
);

/* Solid gray heart with white stroke border — no circle background */
const HEART_SOLID = (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
      fill="#C8C8C8"
      stroke="white"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const ListingPreview = ({ formik }) => {
  const { t } = useTranslation();
  const { locale } = useSelector((state) => state?.app?.AppConfig);
  const { user } = useSelector((state) => state.app.loginUser);

  const values = formik?.values || {};

  const [gallery, setGallery] = useState({ open: false, tab: 'images' });
  const [imgIndex, setImgIndex] = useState(0);

  // Live clock for the mock status bar (12-hour, no AM/PM — iOS style), refreshed every 30s.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const statusTime = useMemo(() => {
    const hours = now.getHours() % 12 || 12;
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }, [now]);
  const videos = values?.videos;
  const hasVideos = (videos || []).some((v) => !v?._destroy && (v?.url || v?.link));

  const getImgUrl = (img) => img?.large || img?.gallerythumb || img?.url || img?.thumbnail || img?.image || img?.imageURL || img?.localPreview || null;

  const usableImages = useMemo(() => {
    const images = values?.property_images;
    if (!images?.length) return [];
    return images.filter((img) => !img?.inError && !img?._destroy && getImgUrl(img));
  }, [values?.property_images]);

  const hasImages = usableImages.length > 0;
  const clampedIndex = Math.min(imgIndex, Math.max(0, usableImages.length - 1));
  const coverImage = usableImages.length ? getImgUrl(usableImages[clampedIndex]) : null;

  const openGallery = (tab) => {
    if (!hasImages && !hasVideos) return;
    setGallery({ open: true, tab });
  };

  const goLeft = (e) => {
    e.stopPropagation();
    if (usableImages.length > 1) setImgIndex((i) => (i - 1 + usableImages.length) % usableImages.length);
  };

  const goRight = (e) => {
    e.stopPropagation();
    if (usableImages.length > 1) setImgIndex((i) => (i + 1) % usableImages.length);
  };

  const title = useMemo(() => {
    if (locale === 'ar') {
      return values?.property_title_ar || values?.title_l1 || values?.property_title_en || values?.title || '';
    }
    return values?.property_title_en || values?.title || values?.property_title_ar || values?.title_l1 || '';
  }, [values?.property_title_en, values?.property_title_ar, values?.title, values?.title_l1, locale]);

  const price = useMemo(() => {
    const raw = values?.price;
    if (!raw && raw !== 0) return null;
    return formatNumberString(raw, { maximumFractionDigits: 0 });
  }, [values?.price]);

  const isDailyRental =
    values?.listing_purpose?.slug === 'daily-rental' ||
    values?.listing_category?.purpose_hash?.slug === 'daily-rental';

  // Discounted price (sale discount functionality): show the discounted price with
  // the original price struck through in red.
  const discountedPrice = useMemo(() => {
    const raw = values?.discounted_price;
    if (!values?.property_discount_enabled || raw == null || raw === '') return null;
    return formatNumberString(raw, { maximumFractionDigits: 0 });
  }, [values?.property_discount_enabled, values?.discounted_price]);

  const beds = values?.bedrooms;
  const baths = values?.bathrooms;
  const area = values?.area;

  const locationLabel = useMemo(() => {
    const loc = values?.['location-info'];
    const parts = [
      loc?.location?.title ? tenantUtils.getLocalisedString(loc.location, 'title') : null,
      loc?.city?.title ? tenantUtils.getLocalisedString(loc.city, 'title') : loc?.city?.name || null,
    ].filter(Boolean);
    return parts.join(', ') || null;
  }, [values?.['location-info']]);

  // Room listings carry a stale/default beds value (e.g. "Studio") in Formik even though
  // beds isn't applicable — don't show the beds row for the Room property type. Match on
  // id / external_id / slug / (localized) name so it works regardless of API id shape.
  const propertyTypeObj = values?.listing_type || values?.sub_listing_type || values?.listing_category || {};
  const propertyTypeSlug = String(propertyTypeObj?.slug || propertyTypeObj?.htaccess || '').toLowerCase().trim();
  const propertyTypeName = String(
    values?.property_type_label ||
      tenantUtils.getLocalisedString(propertyTypeObj, 'title') ||
      tenantUtils.getLocalisedString(propertyTypeObj, 'name') ||
      '',
  )
    .toLowerCase()
    .trim();
  const isRoomPropertyType =
    Number(values?.property_type) === ROOM_PROPERTY_TYPE_ID ||
    Number(propertyTypeObj?.external_id) === ROOM_PROPERTY_TYPE_ID ||
    propertyTypeSlug === 'room' ||
    propertyTypeName === 'room' ||
    propertyTypeName === 'غرفة';

  const hasBeds = !isRoomPropertyType && beds !== undefined && beds !== null && beds !== '';
  const hasBaths = baths !== undefined && baths !== null && baths !== '';
  const hasArea = area !== undefined && area !== null && area !== '';

  const avatar = user?.profile_image || user?.image || user?.avatar || null;

  return (
    <PreviewWrapper>
      <PreviewTitle>{t('Your Listing will look like this')}</PreviewTitle>

      <Phone>
        <FrameOverlay src={PHONE_FRAME} alt="" aria-hidden="true" />
        <StatusBar aria-hidden="true">
          <span className="left">
            {statusTime}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
                stroke="#1d1d1f"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" stroke="#1d1d1f" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M3 3l18 18" stroke="#1d1d1f" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </span>
          <span className="right">
            {/* Cellular signal */}
            <svg width="16" height="10" viewBox="0 0 16 10" fill="none" aria-hidden="true">
              <path d="M0.925751 6.154H1.8515C2.09703 6.154 2.33248 6.25125 2.50609 6.42434C2.6797 6.59744 2.77725 6.8322 2.77725 7.077V8.923C2.77725 9.1678 2.6797 9.40256 2.50609 9.57566C2.33248 9.74875 2.09703 9.846 1.8515 9.846H0.925751C0.680228 9.846 0.444771 9.74875 0.27116 9.57566C0.0975486 9.40256 0 9.1678 0 8.923V7.077C0 6.8322 0.0975486 6.59744 0.27116 6.42434C0.444771 6.25125 0.680228 6.154 0.925751 6.154ZM5.24659 4.308H6.17234C6.41786 4.308 6.65332 4.40524 6.82693 4.57834C7.00054 4.75144 7.09806 4.98621 7.09806 5.231V8.924C7.09806 9.16879 7.00054 9.40356 6.82693 9.57666C6.65332 9.74976 6.41786 9.847 6.17234 9.847H5.24858C5.00305 9.847 4.7676 9.74976 4.59398 9.57666C4.42037 9.40356 4.32282 9.16879 4.32282 8.924V5.233C4.32256 5.11162 4.34633 4.99138 4.39273 4.87917C4.43914 4.76695 4.50729 4.66497 4.59328 4.57905C4.67927 4.49313 4.78141 4.42496 4.89386 4.37845C5.0063 4.33194 5.12684 4.308 5.24858 4.308H5.24659ZM9.56739 2.154H10.4931C10.7387 2.154 10.9741 2.25125 11.1477 2.42434C11.3213 2.59744 11.4189 2.8322 11.4189 3.077V8.924C11.4189 9.16879 11.3213 9.40356 11.1477 9.57666C10.9741 9.74976 10.7387 9.847 10.4931 9.847H9.56739C9.32187 9.847 9.08641 9.74976 8.9128 9.57666C8.73919 9.40356 8.64164 9.16879 8.64164 8.924V3.077C8.64164 2.95571 8.66564 2.8356 8.71222 2.72355C8.75881 2.6115 8.82707 2.50971 8.91314 2.42399C8.99921 2.33826 9.1014 2.2703 9.21383 2.22397C9.32627 2.17765 9.44675 2.15387 9.5684 2.154H9.56739ZM13.8882 0H14.814C15.0595 0 15.295 0.0972439 15.4686 0.27034C15.6422 0.443436 15.7397 0.678205 15.7397 0.923V8.923C15.7397 9.1678 15.6422 9.40256 15.4686 9.57566C15.295 9.74875 15.0595 9.846 14.814 9.846H13.8882C13.6427 9.846 13.4072 9.74875 13.2336 9.57566C13.06 9.40256 12.9625 9.1678 12.9625 8.923V0.923C12.9625 0.678205 13.06 0.443436 13.2336 0.27034C13.4072 0.0972439 13.6427 0 13.8882 0Z" fill="#222222" />
            </svg>
            {/* Wi-Fi */}
            <svg width="15" height="11" viewBox="0 0 15 11" fill="none" aria-hidden="true">
              <path d="M7.06797 2.09483C9.12182 2.09503 11.0969 2.88273 12.5843 4.29483C12.6377 4.34759 12.7098 4.37719 12.7849 4.37719C12.8601 4.37719 12.9322 4.34759 12.9856 4.29483L14.0557 3.21783C14.1112 3.16154 14.1422 3.08573 14.142 3.00683C14.1411 2.92784 14.109 2.85238 14.0527 2.79683C12.1741 1.002 9.67285 0 7.071 0C4.46915 0 1.96786 1.002 0.0892857 2.79683C0.0329616 2.85238 0.00086604 2.92784 9.45575e-07 3.00683C-0.000197322 3.08573 0.0307909 3.16154 0.0862554 3.21783L1.15746 4.29483C1.21081 4.34759 1.28289 4.37719 1.35803 4.37719C1.43318 4.37719 1.50529 4.34759 1.55864 4.29483C3.04605 2.88273 5.02116 2.09503 7.07501 2.09483H7.06797ZM7.06797 5.59483C8.19528 5.59471 9.28239 6.01243 10.118 6.76683C10.1724 6.81821 10.2447 6.84643 10.3197 6.8455C10.3946 6.84457 10.4662 6.81455 10.5192 6.76183L11.5884 5.68483C11.6161 5.65671 11.638 5.62343 11.6528 5.58688C11.6676 5.55034 11.675 5.51124 11.6747 5.47183C11.6741 5.4324 11.6657 5.39347 11.6501 5.35727C11.6344 5.32106 11.6118 5.28829 11.5834 5.26083C10.3569 4.12426 8.74444 3.49253 7.06999 3.49253C5.39554 3.49253 3.78311 4.12426 2.5566 5.26083C2.52822 5.28829 2.50556 5.32106 2.4899 5.35727C2.47424 5.39347 2.46588 5.4324 2.46532 5.47183C2.4651 5.51131 2.47269 5.55045 2.48767 5.587C2.50264 5.62355 2.5247 5.6568 2.55259 5.68483L3.61372 6.76183C3.66679 6.81455 3.73841 6.84457 3.81332 6.8455C3.88823 6.84643 3.96056 6.81821 4.01494 6.76683C4.85007 6.01289 5.93637 5.5952 7.06298 5.59483H7.06797ZM9.12308 8.16583C9.15148 8.13817 9.17388 8.105 9.18889 8.06836C9.2039 8.03172 9.2112 7.9924 9.21035 7.95283C9.20893 7.91299 9.19929 7.87387 9.18203 7.83791C9.16478 7.80195 9.14027 7.76992 9.11004 7.74383C8.53804 7.26148 7.81311 6.99681 7.06396 6.99681C6.31481 6.99681 5.58991 7.26148 5.01791 7.74383C4.98768 7.76992 4.96318 7.80195 4.94592 7.83791C4.92866 7.87387 4.91902 7.91299 4.91761 7.95283C4.91675 7.9924 4.92406 8.03172 4.93906 8.06836C4.95407 8.105 4.97648 8.13817 5.00487 8.16583L6.85436 10.0268C6.90752 10.0802 6.9795 10.1107 7.05493 10.1118C7.13036 10.1107 7.20237 10.0802 7.25554 10.0268L9.12308 8.16583Z" fill="#222222" />
            </svg>
            {/* Battery */}
            <svg width="23" height="11" viewBox="0 0 23 11" fill="none" aria-hidden="true">
              <g opacity="0.35">
                <path fillRule="evenodd" clipRule="evenodd" d="M2.46924 1C1.66072 1 1.00298 1.65581 1.00298 2.46192V8.00125C1.00298 8.80736 1.66072 9.46318 2.46924 9.46318H17.9021C18.7106 9.46318 19.3683 8.80736 19.3683 8.00125V2.46192C19.3683 1.65581 18.7106 1 17.9021 1H2.46924ZM2.46924 0H17.9021C19.2658 0 20.3713 1.10224 20.3713 2.46192V8.00125C20.3713 9.36094 19.2658 10.4632 17.9021 10.4632H2.46924C1.10551 10.4632 0 9.36094 0 8.00125V2.46192C0 1.10224 1.10551 0 2.46924 0Z" fill="#222222" />
              </g>
              <path d="M21.2976 3.38477V7.07777C21.6622 6.92518 21.9735 6.66853 22.1923 6.34009C22.4111 6.01164 22.5276 5.62605 22.5273 5.23177C22.5278 4.83731 22.4114 4.4515 22.1926 4.12286C21.9738 3.79422 21.6624 3.53742 21.2976 3.38477Z" fill="#222222" />
              <path d="M3.08597 1.8457H17.2841C17.6116 1.8457 17.9256 1.9754 18.1572 2.20625C18.3887 2.43711 18.5188 2.75022 18.5188 3.0767V7.3847C18.5188 7.71118 18.3887 8.02429 18.1572 8.25515C17.9256 8.48601 17.6116 8.6157 17.2841 8.6157H3.08597C2.75869 8.6157 2.44481 8.48615 2.21329 8.2555C1.98178 8.02486 1.85158 7.71201 1.85132 7.3857V3.0777C1.85119 2.91596 1.88302 2.75578 1.94501 2.60631C2.007 2.45684 2.09793 2.32102 2.21259 2.20661C2.32725 2.09219 2.46339 2.00143 2.61325 1.9395C2.76312 1.87758 2.92375 1.8457 3.08597 1.8457Z" fill="#222222" />
            </svg>
          </span>
        </StatusBar>
        <Screen>
          <Cover
            $src={coverImage}
            onClick={() => openGallery(hasImages ? 'images' : 'videos')}
            style={{ cursor: hasImages || hasVideos ? 'pointer' : 'default' }}
          >
            {!coverImage && <CoverPlaceholder>{COVER_PLACEHOLDER}</CoverPlaceholder>}
            <NavArrow $side="left" onClick={goLeft}>‹</NavArrow>
            <NavArrow $side="right" onClick={goRight}>›</NavArrow>
            {user?.is_tru_broker && !isDailyRental && (
              <TruBroker>
                <span className="av" style={avatar ? { '--tb-av': `url(${avatar})` } : undefined} />
                <span className="tb-txt">
                  Tru<span className="tb-b">Broker</span><span className="tb-tm">™</span>
                </span>
              </TruBroker>
            )}
            {usableImages.length > 0 && (
              <ImageCounter>{clampedIndex + 1}/{usableImages.length}</ImageCounter>
            )}
            <Dots>
              {usableImages.length > 1
                ? usableImages.slice(0, 5).map((_, i) => (
                    <span key={i} className={i === clampedIndex ? 'active' : ''} />
                  ))
                : [0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className={i === 0 ? 'active' : ''} />
                  ))}
            </Dots>
            <Heart>{HEART_SOLID}</Heart>
          </Cover>

          <CardContent>
            {price ? (
              <PriceText>
                {discountedPrice ? (
                  <>
                    {tenantConstants.CURRENCY_SYMBOL()} {discountedPrice}
                    <span
                      style={{
                        color: '#9D9D9D',
                        textDecoration: 'line-through',
                        textDecorationColor: '#E5100E',
                        fontWeight: 500,
                        fontSize: 13,
                      }}
                    >
                      {tenantConstants.CURRENCY_SYMBOL()} {price}
                    </span>
                  </>
                ) : (
                  <>
                    {tenantConstants.CURRENCY_SYMBOL()} {price}
                  </>
                )}
              </PriceText>
            ) : (
              <PriceText style={{ color: '#bbb' }}>{t('Price')}</PriceText>
            )}

            {(hasBeds || hasBaths || hasArea) && (
              <MetaRow>
                {hasBeds && (
                  <span>
                    {BED_ICON}
                    {t(String(beds))}
                  </span>
                )}
                {hasBaths && (
                  <span>
                    {BATH_ICON}
                    {baths}
                  </span>
                )}
                {hasArea && (
                  <span>
                    {AREA_ICON}
                    {area} {t('sqm')}
                  </span>
                )}
              </MetaRow>
            )}

            {title ? (
              <TitleText>{title}</TitleText>
            ) : (
              <TitleText style={{ color: '#bbb' }}>{t('Listing title will appear here')}</TitleText>
            )}

            {locationLabel && (
              <LocationRow>
                {LOCATION_PIN}
                <span>{locationLabel}</span>
              </LocationRow>
            )}

            <ContactRow aria-hidden="true">
              <PillBtn $variant="teal">
                <Icon icon="MdEmail" size={14} color="#006169" />
                {t('Email_short')}
              </PillBtn>
              <PillBtn $variant="teal">
                {PHONE_ICON}
                {t('Call')}
              </PillBtn>
              <PillBtn $variant="whatsapp">
                {WHATSAPP_ICON}
              </PillBtn>
            </ContactRow>
          </CardContent>
        </Screen>
      </Phone>

      <MediaGalleryModal
        visible={gallery.open}
        initialTab={gallery.tab}
        onClose={() => setGallery((g) => ({ ...g, open: false }))}
        images={values?.property_images || []}
        videos={videos || []}
      />
    </PreviewWrapper>
  );
};

export default ListingPreview;
